/**
 * Valori derivati dal codice (soglie della specifica, replicate dal riferimento).
 * Condivisi fra API e front-end perché devono dare lo stesso risultato.
 */
/**
 * Etichetta del FameIndex derivata dal VALORE, come già quella di
 * FameIntegrity.
 *
 * Fino al 16/10 si leggeva `fameIndex.label` dal JSON, quindi restava in
 * italiano anche con l'interfaccia in inglese. La scala è quella fissata dal
 * committente nel feedback (Level 1, punto 5), e le soglie valgono come
 * **limite inferiore**: da 2,00 "Nella media", da 3,00 "Buono", da 3,70
 * "Ottimo", da 4,40 "Eccellente", sotto 2,00 "Basso".
 *
 * Il valore si confronta così com'è, senza arrotondarlo prima: nel JSON ha
 * sempre due decimali, e un arrotondamento a una cifra porterebbe 3,69 a 3,7,
 * cambiando fascia.
 *
 * Il campo `fameIndex.label` resta nel contratto dati, validato e conservato,
 * e partecipa ancora alla regola [CRITICA] di propagazione del N.D.
 * (`brand.ts`): non è codice morto, semplicemente non si rende più.
 */
export function fameIndexLabelKey(value) {
    if (value === null)
        return 'nd';
    if (value < 2.0)
        return 'low';
    if (value < 3.0)
        return 'average';
    if (value < 3.7)
        return 'good';
    if (value < 4.4)
        return 'excellent';
    return 'outstanding';
}
/**
 * §3 — colore del FameIndex derivato dal VALORE.
 * Il campo `fameIndex.color` del JSON è ridondante e non va letto.
 */
export function fameIndexColor(value) {
    if (value === null)
        return 'gray';
    if (value < 2.0)
        return 'red';
    if (value < 3.0)
        return 'orange';
    if (value < 3.7)
        return 'amber';
    return 'green';
}
/**
 * §3 — FameIntegrity: label e colore derivano dal numero, nel JSON non esiste
 * alcuna label. Sotto 20 e fra 20 e 49 la label è la stessa ("Non Conforme"),
 * cambia solo il colore: è voluto, segnala la gravità senza cambiare verdetto.
 */
export function fameIntegrityBand(value) {
    if (value === null)
        return { labelKey: 'nd', sublabelKey: 'nd', color: 'gray', tier: -1 };
    if (value < 20) {
        return { labelKey: 'nonConforme', sublabelKey: 'esposizioneCritica', color: 'red', tier: 0 };
    }
    if (value < 50) {
        return {
            labelKey: 'nonConforme',
            sublabelKey: 'esposizioneSignificativa',
            color: 'orange',
            tier: 1,
        };
    }
    if (value < 80) {
        return {
            labelKey: 'preCertificazione',
            sublabelKey: 'percorsoConformita',
            color: 'amber',
            tier: 2,
        };
    }
    return { labelKey: 'certificabile', sublabelKey: 'conforme', color: 'green', tier: 3 };
}
/**
 * §4 — badge BasicLink, derivato da `basicLinkRate` (solo Trustpilot).
 * La parola "BasicLink" non compare mai in interfaccia: il badge mostra una
 * label neutra, questa funzione restituisce solo il livello.
 */
export function basicLinkLevel(rate) {
    if (rate === undefined)
        return null;
    if (rate < 3)
        return 1;
    if (rate < 20)
        return 2;
    return 3;
}
/**
 * §6 — quadrante della matrice (Level 3): posizione geometrica sugli assi
 * FameIntegrity × FameIndex. È distinto da `matrixLabel`, che è il verdetto di
 * conformità. I brand con FameIndex N.D. non hanno quadrante: la dashboard li
 * posiziona con jitter e mostra il matrixLabel come badge.
 */
export function matrixQuadrant(fameIntegrity, fameIndex) {
    if (fameIndex === null || fameIntegrity === null)
        return null;
    const integro = fameIntegrity >= 50;
    const indiceAlto = fameIndex >= 3.0;
    if (integro)
        return indiceAlto ? 'areaSicura' : 'rischioOperativo';
    return indiceAlto ? 'rischioRegolatorio' : 'rischioCritico';
}
/** §6 — label dei touchpoint, derivate dallo status. */
export function touchpointLabelKey(status) {
    return `touchpoint.${status}`;
}
/** §8 — il grafico trend appare solo con almeno due snapshot. */
export function showsTrendChart(historyLength) {
    return historyLength >= 2;
}
/**
 * §10 — benchmark di settore: sotto i 3 brand il Level 3 mostra un avviso e non
 * i benchmark. La specifica ha due indicazioni divergenti (avviso sotto 3 brand
 * nel testo, `averages: null` con 1 solo brand nella checklist): si applica la
 * regola dei 3, coerente col testo dell'avviso nella dashboard di riferimento.
 */
export const MIN_BRANDS_FOR_BENCHMARK = 3;
/**
 * §5 — medie di settore derivate dai brand a database (`verticals` non si
 * importa). Media aritmetica semplice, FameIndex calcolato escludendo i brand
 * N.D., arrotondamento a 2 decimali e integrity all'intero.
 *
 * Assunzione da confermare.
 */
export function sectorAverages(brands) {
    if (brands.length === 0)
        return null;
    const round2 = (n) => Math.round(n * 100) / 100;
    const mean = (values) => values.reduce((a, b) => a + b, 0) / values.length;
    const conIndice = brands.map((b) => b.fameIndex).filter((v) => v !== null);
    return {
        webScore: round2(mean(brands.map((b) => b.webScore))),
        fameIntegrity: Math.round(mean(brands.map((b) => b.fameIntegrity))),
        fameIndex: conIndice.length > 0 ? round2(mean(conIndice)) : null,
    };
}
//# sourceMappingURL=derived.js.map